import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../../../lib/api';

const API_BASE = '/api';

/**
 * Fetch aggregated data from a Tool's submissions, for progress blocks and charts.
 *
 * Picks the endpoint based on what's provided:
 *   - `slug` → public endpoint (/api/public/tools/:slug/...) — no auth, works on live landing page
 *   - `toolId` → authenticated endpoint (/analytics/tools/:id/...) — used in the editor preview
 *
 * `kind` selects the shape of the result:
 *   - 'aggregate'   → { value: number }
 *   - 'timeseries'  → { data: Array<{key,value}> }
 *   - 'distribution'→ { data: Array<{key,value}> }
 *
 * Passing `pollSeconds > 0` enables live polling (use sparingly — each tick hits the DB).
 */
export type FetchKind = 'aggregate' | 'timeseries' | 'distribution';

export interface UseToolDataOptions {
  kind: FetchKind;
  toolId?: string;
  toolSlug?: string;
  spec: Record<string, any>;
  /** Seconds between polls; 0 = fetch once */
  pollSeconds?: number;
  /** Skip fetching entirely (e.g. when source = static) */
  skip?: boolean;
}

export interface UseToolDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export function useToolData<T = any>(opts: UseToolDataOptions): UseToolDataResult<T> {
  const { kind, toolId, toolSlug, spec, pollSeconds = 0, skip } = opts;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const specKey = JSON.stringify(spec);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (skip) { setData(null); return; }
    if (!toolId && !toolSlug) { setData(null); return; }

    let cancelled = false;
    let timer: any = null;

    // Prefer the public endpoint whenever we have a slug — public pages
    // should ALWAYS read the same data regardless of who's logged in.
    // Fall back to the authenticated endpoint only when there's no slug
    // (i.e. inside the editor, previewing a draft/inactive tool by id).
    const token = localStorage.getItem('token') || localStorage.getItem('authToken') || '';
    const preferAuth = !!token && !!toolId && !toolSlug;

    // Cache-bust every request — a unique `_t` query param + explicit
    // no-cache headers defeat any browser/service-worker/CDN caching so
    // the user sees fresh submission counts without opening an incognito
    // window each time.
    const noCache = {
      params: { _t: Date.now() },
      headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' },
    };

    const fetchOnce = async () => {
      setLoading(true);
      setError(null);
      const endpoint = preferAuth ? `auth/${toolId}/${kind}` : `public/${toolSlug}/${kind}`;
      console.debug('[useToolData] →', endpoint, spec);
      try {
        let resp: any;
        if (preferAuth) {
          resp = await api.post(`/analytics/tools/${toolId}/${kind}`, spec, noCache);
        } else if (toolSlug) {
          resp = await axios.post(`${API_BASE}/public/tools/${toolSlug}/${kind}`, spec, noCache);
        } else if (toolId) {
          resp = await api.post(`/analytics/tools/${toolId}/${kind}`, spec, noCache);
        }
        if (cancelled || !mountedRef.current) return;
        console.debug('[useToolData] ←', endpoint, resp?.data);
        if (kind === 'aggregate') setData(resp.data as T);
        else setData(resp.data as T);
      } catch (e: any) {
        if (cancelled || !mountedRef.current) return;
        console.warn('[useToolData] ✗', endpoint, e?.response?.status, e?.response?.data);
        if (preferAuth && toolSlug) {
          try {
            const resp2 = await axios.post(`${API_BASE}/public/tools/${toolSlug}/${kind}`, spec, noCache);
            if (cancelled || !mountedRef.current) return;
            console.debug('[useToolData] ← (fallback public)', resp2?.data);
            setData(resp2.data as T);
            setError(null);
            return;
          } catch (e2: any) {
            setError(e2?.response?.data?.error || e.message || 'שגיאה בטעינת נתונים');
            return;
          }
        }
        setError(e?.response?.data?.error || e.message || 'שגיאה בטעינת נתונים');
      } finally {
        if (!cancelled && mountedRef.current) setLoading(false);
      }
    };

    fetchOnce();
    if (pollSeconds > 0) {
      timer = setInterval(fetchOnce, pollSeconds * 1000);
    }

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, toolId, toolSlug, specKey, pollSeconds, skip]);

  return { data, loading, error };
}
